import { memo, useCallback } from "react";

import { Checkbox } from "@/components/Checkbox";
import Icon from "@/components/Icon/Icon";
import { HStack } from "@/components/Layout";

import type { FieldRenderInputProps } from "../../types";

import { BaseField, type BaseFieldProps } from "./BaseField";

export const ChangeCheckboxField = memo(
  ({
    fieldId,
    field,
    readOnly,
    disabled,
    className,
    "data-testid": testId,
  }: BaseFieldProps) => {
    // 入力レンダリング関数
    const renderInput = useCallback(
      (props: FieldRenderInputProps) => {
        // 値をブール型に変換
        const boolValue =
          typeof props.value === "string"
            ? props.value.toLowerCase() === "true"
            : Boolean(props.value);
        const previousBoolValue =
          typeof props.previousValue === "string"
            ? props.previousValue.toLowerCase() === "true"
            : Boolean(props.previousValue);

        // 入力変更ハンドラー
        const handleCheckedChange = (checked: boolean) => {
          props.onChange(checked);
        };

        return (
          <HStack className="items-center justify-center w-full gap-4">
            {/* 現在の値（読み取り専用の表示） */}
            <div className="flex-1 justify-start min-w-0">
              <Checkbox
                id={props.id}
                name={props.name}
                checked={previousBoolValue}
                readOnly={true}
                disabled={true}
                label={field?.label}
              />
            </div>

            {/* Arrow Icon */}
            <div className="items-center shrink-0 self-center pr-3 pl-3">
              <Icon
                iconName="ArrowBigRightIcon"
                className="w-4 h-4 text-primary-on-neutral-fill"
              />
            </div>

            {/* 新しい値の入力 */}
            <div className="flex-1 min-w-0">
              <Checkbox
                id={props.id}
                name={props.name}
                checked={boolValue}
                onCheckedChange={
                  props.disabled || props.readOnly
                    ? undefined
                    : handleCheckedChange
                }
                onFocus={props.onFocus}
                onBlur={props.onBlur}
                disabled={props.disabled}
                readOnly={props.readOnly}
                required={props.required}
                aria-invalid={!!props.error}
                aria-describedby={props.error ? `${props.id}-error` : undefined}
                label={field?.label}
              />
            </div>
          </HStack>
        );
      },
      [field?.label],
    );

    // BaseFieldを使用してチェックボックスフィールドをレンダリング
    return (
      <BaseField
        fieldId={fieldId}
        field={field}
        layout="vertical" // UI 的にラベルと入力フィールドが縦並びじゃないとしんどいので
        readOnly={readOnly}
        disabled={disabled}
        className={className}
        data-testid={testId}
        renderInput={renderInput}
      />
    );
  },
);

ChangeCheckboxField.displayName = "ChangeCheckboxField";
